import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LifeEventGeneric } from './LifeEventGeneric'
import { lifeEventApi } from './life-event-api'
import { ActionBar } from './action-bar'
import Layout from '../common/Layout'
import SharingScope from '../common/SharingScope'

// Category mapping
const CATEGORY_MAP = {
  1: 'Early Life',
  2: 'Education',
  3: 'Career',
  4: 'Relationships',
  5: 'Life Changes'
}

function mapEventTemplate(template) {
  return {
    id: template.id,
    name: template.name,
    category: template.category,
    description: template.description || '',
    required_fields: template.required_fields || {},
    optional_fields: template.optional_fields || {},
    story_prompts: template.story_prompts || [],
    suggested_roles: template.suggested_roles || {},
    media_types: template.media_types || { required: [], suggested: [] },
    is_custom: template.is_custom || false
  }
}

export function CreateEventGeneric() {
  const navigate = useNavigate()
  const [eventTypes, setEventTypes] = useState([])
  const [selectedEventType, setSelectedEventType] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({})
  const [sharingScope, setSharingScope] = useState('single_family')
  const [sharingFamilyIds, setSharingFamilyIds] = useState([])

  // Single API call to fetch templates
  useEffect(() => {
    async function fetchEventTypes() {
      try {
        const data = await lifeEventApi.getEventTypes()
        // Map the API response to our event structure
        const mappedEvents = data.map(mapEventTemplate)
        setEventTypes(mappedEvents)
      } catch (err) {
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchEventTypes()
  }, [])

  function handleFormDataChange(newData) {
    setFormData(prevData => {

      // If the update includes story_responses, merge them properly
      if (newData.story_responses) {
        const updatedData = {
          ...prevData,
          story_responses: {
            ...(prevData.story_responses || {}),
            ...newData.story_responses
          }
        }
        return updatedData
      }

      // Otherwise, just merge the new data
      const updatedData = {
        ...prevData,
        ...newData
      }
      return updatedData
    })
  }

  async function handleSubmit() {
    if (!selectedEventType) return
    
    setIsSubmitting(true)
    setError(null)

    try {
      const eventData = {
        template: selectedEventType.id,
        category: selectedEventType.category,
        title: selectedEventType.name,
        date: formData.date,
        location: formData.location,
        weight: formData.weight,
        height: formData.height,
        story_details: formData.story_details || {},
        status: 'draft',
        sharing_scope: sharingScope,
        family_ids: sharingFamilyIds,
      }

      await lifeEventApi.createLifeEvent(eventData)
      navigate('/dashboard')
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F5DC]">
        <main className="space-y-8">
          <h1 className="text-4xl font-bold mb-8">Create Life Event</h1>

          <div className="max-w-7xl mx-auto">
            {error && (
              <div className="mb-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              </div>
            )}

            <LifeEventGeneric
              eventData={selectedEventType}
              formData={formData}
              onFormDataChange={handleFormDataChange}
              onEventSelect={setSelectedEventType}
              templates={eventTypes}
              isLoading={isLoading}
            />

            {/* Sharing Scope */}
            <SharingScope
              value={sharingScope}
              onChange={(scope, familyIds) => {
                setSharingScope(scope);
                setSharingFamilyIds(familyIds);
              }}
              className="mt-4"
            />
          </div>
        </main>

        <ActionBar 
          onBack={() => window.history.back()}
          onNext={handleSubmit}
          showBack={true}
          showNext={!!selectedEventType}
          disableNext={isSubmitting || !formData.date || !formData.location}
          isSubmitting={isSubmitting}
          nextText="Create Event"
        />
      </div>
    </Layout>
  )
}